import { ComposerContext, ComposerContextType } from './ComposerContext';
import { RECIPIENT_TYPES } from 'reducers/roleBasedMessagingSearch';
import { MODAL_FILE_CONTEXT_KEY } from 'reducers/fileUploads';
// @ts-ignore
import SelectRecipientPanel, { SelectRecipientTabs } from './SelectRecipientPanel';

import { Backbone$Id } from 'types/typescript/Backbone';
import ChatStore from 'chat/stores/ChatStore';
import Chats from 'collections/Chats';
// @ts-ignore
import ComposeMessage from './ComposeMessage';
import { DragDropContext } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import { Message } from 'entities/Message';
import { OrderedMap } from 'immutable';
import PotentialChatMember from 'entities/PotentialChatMember';
import React from 'react';
// @ts-ignore
import SplitComposerHeader from './SplitComposerHeader';
import { Target } from 'entities/Message';
import { UploadRequestData } from 'reducers/fileUploads';
import { isEqual } from 'lodash';

import { Recipient, ComposerGroup, ComposerOrganization } from 'types/typescript/messagingTypes';

export type MessageType = 'urgent' | 'message';

type GQLProps = {
  groupsLoading: boolean;
  groups?: ComposerGroup[] | null;

  // graphQL
  orgsLoading: boolean;
  organizations?: ComposerOrganization[];
  schools?: ComposerOrganization[];
};

export type ReduxDispatchProps = {
  onFlush: () => void;
  clearSelection: () => void;
  removeFile: (fileContextKey: string, filename: string) => void;
  onSelectInitialTargets: (targets: Target[]) => void;
  onLoadOrganizations: (organizations: ComposerOrganization[]) => void;
  onLoadGroups: (groups: ComposerGroup[]) => void;
  onLoadGroupChats: (chats: Chats) => void;
  onLoadIndividuals: (members: PotentialChatMember[]) => void;
  onLoadPotentialChatMembers: (query: string) => void;
  onSearchGroupChats: (query: string) => void;
};

export type ReduxStateProps = {
  savedMessage: string;
  fileUploads: OrderedMap<string, UploadRequestData>;
  selection: Recipient[];

  loadingGroupChats: boolean;
  loadingIndividualRecipients: boolean;
};

export type GeneratedProps = GQLProps & ReduxDispatchProps & ReduxStateProps;
export type RequiredProps = {
  defaultTab?: SelectRecipientTabs;
  draftAnnouncement?: Message;
  initialTargets?: Target[];
  onSignificantChangeMade?: () => void;
  isEditingScheduledMessage?: boolean;
  onRequestHide?: () => void;
  onDismiss?: () => void;
  skipSelectRecipients: boolean;
  onMessageSent?: () => void;
  onPageChange?: (pageKey: 'share_to' | 'composer') => void;
  messageType: MessageType;
  shareSdkBodyUrl?: string;
};
type Props = RequiredProps & GeneratedProps;

type State = {
  page: number;
  studentIdListCreated: boolean;
};

class SplitComposer extends React.Component<Props, State> {
  static defaultProps = {
    messageType: 'message',
  };

  constructor(props: Props) {
    super(props);
    const studentIdListRecipientInSelection = props.selection.some(
      s => s.type === RECIPIENT_TYPES.STUDENT_ID_LIST_RECIPIENT
    );

    this.state = {
      page: props.skipSelectRecipients ? 2 : 1,
      studentIdListCreated: studentIdListRecipientInSelection,
    };
  }

  componentDidMount() {
    const {
      initialTargets,
      onLoadOrganizations,
      onLoadGroups,
      onLoadGroupChats,
      onLoadIndividuals,
      onSearchGroupChats,
      onLoadPotentialChatMembers,
      organizations,
      groups,
    } = this.props;

    if (organizations) {
      onLoadOrganizations(organizations);
    }

    if (groups) {
      onLoadGroups(groups);
    }

    // Make sure we show available group chats.
    const groupChats = ChatStore.getGroupChats();
    if (groupChats.length === 0) {
      onSearchGroupChats('');
    } else {
      onLoadGroupChats(groupChats);
    }

    // Make sure we have some people to show.
    const individuals = ChatStore.getEligibleMemberModels();
    if (individuals.length === 0) {
      onLoadPotentialChatMembers('');
    } else {
      onLoadIndividuals(individuals);
    }

    if (initialTargets) {
      this.props.onSelectInitialTargets(initialTargets);
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const groupsWereNotPreviouslyLoaded = !prevProps.groups;
    const orgsWereNotPreviouslyLoaded = !prevProps.organizations;
    const prevGroupUuids = prevProps.groups ? prevProps.groups.map(group => group.uuid) : [];
    const newGroupUuids = this.props.groups ? this.props.groups.map(group => group.uuid) : [];
    const newGroupsWereLoaded = !isEqual(prevGroupUuids, newGroupUuids);
    const prevOrgUuids = prevProps.organizations
      ? prevProps.organizations.map(org => org.uuid)
      : [];
    const newOrgUuids = this.props.organizations
      ? this.props.organizations.map(org => org.uuid)
      : [];
    const newOrgsWereLoaded = !isEqual(prevOrgUuids, newOrgUuids);
    const { initialTargets } = this.props;

    if (this.props.groups && (groupsWereNotPreviouslyLoaded || newGroupsWereLoaded)) {
      this.props.onLoadGroups(this.props.groups);
      if (initialTargets) {
        this.props.onSelectInitialTargets(initialTargets);
      }
    }

    if (this.props.organizations && (orgsWereNotPreviouslyLoaded || newOrgsWereLoaded)) {
      this.props.onLoadOrganizations(this.props.organizations);
      if (initialTargets) {
        this.props.onSelectInitialTargets(initialTargets);
      }
    }

    const changed =
      prevProps.fileUploads.size !== this.props.fileUploads.size ||
      prevProps.savedMessage.length !== this.props.savedMessage.length ||
      prevState.studentIdListCreated !== this.state.studentIdListCreated ||
      (prevProps.selection.length !== this.props.selection.length && this.props.selection.length);

    if (changed) {
      if (this.props.onSignificantChangeMade) {
        this.props.onSignificantChangeMade();
      }
    }
  }

  componentWillUnmount() {
    this.removeAllFiles();
    this.props.onFlush();
  }

  advanceToComposeMessage = () => {
    this.setState({ page: 2 });
    if (this.props.onPageChange) {
      this.props.onPageChange('composer');
    }
  };

  backToSelectRecipients = () => {
    this.setState({ page: 1 });
    if (this.props.onPageChange) {
      this.props.onPageChange('share_to');
    }
  };

  handleSuccessfulStudentIdListCreation = () => {
    this.setState({ studentIdListCreated: true });
  };

  removeAllFiles = () => {
    const { fileUploads } = this.props;
    fileUploads
      .keySeq()
      .forEach(filename => filename && this.props.removeFile(MODAL_FILE_CONTEXT_KEY, filename));
  };

  render() {
    const {
      onRequestHide,
      selection,
      initialTargets,
      draftAnnouncement,
      isEditingScheduledMessage,
      orgsLoading,
      schools,
      groupsLoading,
      loadingGroupChats,
      loadingIndividualRecipients,
      messageType,
    } = this.props;
    const { page } = this.state;

    const isUrgentMessage = messageType === 'urgent';

    const groupResultsAreLoading = orgsLoading || groupsLoading || loadingGroupChats;
    const initialTarget = initialTargets && initialTargets[0];
    const groupTargetId: Backbone$Id | null | undefined =
      initialTarget && initialTarget.type === 'group_target' ? initialTarget.group_id : null;
    const orgTargetId: Backbone$Id | null | undefined =
      initialTarget && initialTarget.type === 'district_target' ? initialTarget.org_id : null;

    const renderComponentForPage = (composerContext: ComposerContextType) => {
      if (page === 1) {
        return (
          <SelectRecipientPanel
            onContinue={this.advanceToComposeMessage}
            loadingGroupResults={groupResultsAreLoading}
            loadingPeopleResults={loadingIndividualRecipients}
            schools={schools}
            selection={selection}
            onTabChange={this.props.clearSelection}
            onSuccessfulStudentIdListCreation={this.handleSuccessfulStudentIdListCreation}
            defaultTab={this.props.defaultTab}
            initialTargetId={groupTargetId || orgTargetId}
            messageType={messageType}
            isUrgentMessage={isUrgentMessage}
          />
        );
      } else if (page === 2) {
        return (
          <ComposeMessage
            initialTargets={initialTargets}
            draftAnnouncement={draftAnnouncement}
            onMessageSent={this.props.onMessageSent}
            isEditingScheduledMessage={isEditingScheduledMessage}
            onRequestHide={onRequestHide}
            onBack={this.backToSelectRecipients}
            shareSdkBodyUrl={this.props.shareSdkBodyUrl}
            isUrgentMessage={isUrgentMessage}
            UIEventContext={composerContext.getUIEventContext()}
          />
        );
      }

      return null;
    };

    return (
      <React.Fragment>
        <ComposerContext.Provider
          value={{
            isUrgentMessage,
            messageType: 'message', // note this is default and should be changed here in the future
            // eslint-disable-next-line object-shorthand
            getUIEventContext: function() {
              return {
                message_type: this.messageType,
                is_urgent: this.isUrgentMessage,
              };
            },
          }}>
          <SplitComposerHeader messageType={messageType} />
          <ComposerContext.Consumer>
            {(composerContext: ComposerContextType) => renderComponentForPage(composerContext)}
          </ComposerContext.Consumer>
        </ComposerContext.Provider>
      </React.Fragment>
    );
  }
}

// we export this for the SplitComposerModal integration test
// because TargetByStudentIdUploadPage expects a DragDropContext
const SplitComposerWithDnD = DragDropContext(HTML5Backend)(SplitComposer);

export { SplitComposer, SplitComposerWithDnD };

export default SplitComposer;
